import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { VueRenderer } from '@tiptap/vue-3';
import tippy, { followCursor } from 'tippy.js';
import { getRootStyle } from '@/utilities/rootStyles';

let tippyRef = null;
let lastHoverElementId = null;
let hoverComponent = null;
let throttleTimeout = null;

let linkComponent = null;
let linkTippy = null;

export function resetLinkVariables() {
  linkComponent = null;
  linkTippy = null;
}

export const linkHover = (element, editor, linkTooltipComponent) => {
  if (linkComponent || linkTippy) return;

  const text = element.innerText;
  const url = element.getAttribute('href');
  const from = editor.view.posAtDOM(element);
  const to = from + text?.length;

  // create LinkTooltip component instance
  linkComponent = new VueRenderer(linkTooltipComponent, {
    props: {
      url,
      text,
      range: {
        from,
        to,
      },
      editorId: editor.id,
    },
    editor,
  });

  // use tippy to attach component on body element
  linkTippy = tippy(element, {
    appendTo: document.body,
    content: linkComponent.ref.$el,
    showOnCreate: true,
    interactive: true,
    trigger: 'mouseenter',
    maxWidth: 'none',
    animation: 'none',
    role: 'poper',
    onHide() {
      setTimeout(() => {
        editor.commands.hideLinkTooltip();
      }, 0);
    },
    followCursor: 'horizontal',
    plugins: [followCursor],
  });
};

export const getEditorMidlePoint = (editor) => {
  const rootElement = editor.view.dom;
  const rootBoundingRects = rootElement.getBoundingClientRect();

  return rootBoundingRects.left + rootBoundingRects.width / 2;
};

export const blockPosAtCoords = (coords, view) => {
  const pos = view.posAtCoords(coords);
  let node = view.domAtPos(pos.pos);

  node = node.node;
  while (node && node.parentNode) {
    if (node.parentNode?.classList?.contains('ProseMirror')) {
      break;
    }
    node = node.parentNode;
  }

  if (node && node.nodeType === 1) {
    const desc = view.docView.nearestDesc(node, true);
    if (!(!desc || desc === view.docView)) {
      return desc.posBefore;
    }
  }
  return null;
};

export const getBlockElementFromCords = (x, y, getTextNodeFromPoint) => {
  let node = getTextNodeFromPoint(x, y);
  if (!node) {
    node = document.elementFromPoint(x, y);
  }

  // check if node is inside shadow dom (web component)
  if (node?.getRootNode?.() instanceof ShadowRoot) {
    node = node.getRootNode().host;
  }

  while (node && node.parentNode) {
    if (node.parentNode?.classList?.contains('ProseMirror')) {
      break;
    }
    node = node.parentNode;
  }

  if (node && node.nodeType === 1) {
    // check if target node is resizable node view
    if (
      node.getAttribute('data-node-view-wrapper') !== null &&
      node.className.includes('resizable-wrapper')
    ) {
      // get block node from resizable node view
      return node.querySelector('[data-block-id]');
    }
    return node;
  }
  return null;
};

export const getLeftPositionForFullScreenTable = (element) => {
  // get node with tag name table from table wrapper
  const tableNode = element.querySelector('table');
  const tableRect = tableNode.getBoundingClientRect();
  const tableLeft = tableRect.left;

  return tableLeft;
};

export const hover = (element, editor, blockHoverMenuComponent, draggable) => {
  if (!element) return false;
  const blockId = element.getAttribute('data-block-id');
  if (!blockId || !draggable) return false;
  if (blockId === lastHoverElementId) return false;

  lastHoverElementId = blockId;
  hoverComponent?.ref?.$destroy?.();

  hoverComponent = new VueRenderer(blockHoverMenuComponent, {
    props: {
      element,
      editorId: editor.id,
      draggable,
    },
    editor,
  });

  const lineHeight = parseInt(getComputedStyle(element).lineHeight);
  const placement = !lineHeight || element.clientHeight > lineHeight ? 'left-start' : 'left';

  if (!tippyRef?.state?.isDestroyed) {
    // destroy tippy instance only if it is not already destroyed
    tippyRef?.destroy?.();
  }
  editor.commands.setDragHandleBlockId({ blockId });
  // check if the element is table node and is in full screen mode
  // table node has class tableWrapper
  const isTableNode = element.classList.contains('tableWrapper');
  const isTableFullScreen = element.getAttribute('data-content-width') === 'full' || false;

  //check if the element is a resize node
  let isResizeNode = element.getAttribute('data-resize-node') === 'true';
  tippyRef = tippy(element, {
    appendTo: editor.view.dom.parentNode,
    getReferenceClientRect: () => {
      const rect = element.getBoundingClientRect();
      let left = rect.left + parseInt(getRootStyle('--page-elements-side-padding')) || 0;
      if (isTableNode && isTableFullScreen) {
        left = getLeftPositionForFullScreenTable(element);
        // if the element is table node and is in full screen mode use the left position of the table node
        isResizeNode = false;
      }

      return {
        width: rect.width,
        height: rect.height,
        right: rect.right,
        top: rect.top,
        bottom: rect.bottom,
        left: isResizeNode ? rect.left : left,
      };
    },
    popperOptions: {
      boundary: editor.view.dom,
    },
    content: hoverComponent.ref.$el,
    aria: {
      expanded: false,
    },
    showOnCreate: true,
    interactive: true,
    placement,
    role: 'poper',
    maxWidth: 'none',
    animation: 'none',
    hideOnClick: false,
    triggerTarget: editor.view.dom,
    zIndex: 5,
    onHide: () => {
      if (editor.storage.MouseOver.dragHandleBlockId) {
        editor.commands.setDragHandleBlockId({ blockId: '' });
      }
    },
  });
  return true;
};

export const isLinkElementValid = (linkElement) => {
  if (linkElement.nodeName === 'A') {
    if (
      linkElement.getAttribute('data-internal-link') ||
      linkElement.classList.contains('board-list-card-wrap')
    )
      return false;
    return !linkElement.hasAttribute('disable-hover-menu');
  }
  return false;
};

export const findLinkElement = (element) => {
  let linkElement = element;
  while (linkElement && linkElement?.getAttribute?.('data-block-id') === null) {
    if (isLinkElementValid(linkElement)) {
      return linkElement;
    }
    linkElement = linkElement.parentNode;
  }
  return null;
};

export const handleMouseOver = (
  event,
  editor,
  linkTooltipComponent,
  blockHoverMenuComponent,
  getTextNodeFromPoint,
  draggable
) => {
  const { from, to } = editor.view.state.selection;
  if (!(from !== to && editor.isFocused)) {
    const element = document.elementFromPoint(event.x, event.y);

    const linkElement = findLinkElement(element);

    if (linkElement) {
      linkHover(linkElement, editor, linkTooltipComponent);
      return true;
    }
  }

  const xMiddlePoint = getEditorMidlePoint(editor);
  const blockElement = getBlockElementFromCords(xMiddlePoint, event.y, getTextNodeFromPoint);
  return hover(blockElement, editor, blockHoverMenuComponent, draggable);
};

export const MouseOver = ({
  linkTooltipComponent,
  blockHoverMenuComponent,
  getTextNodeFromPoint,
  draggable,
}) =>
  Extension.create({
    name: 'MouseOver',

    addStorage() {
      return {
        dragHandleBlockId: '',
        dragging: false,
      };
    },

    onDestroy() {
      clearTimeout(throttleTimeout);
      if (tippyRef) tippyRef.destroy?.();
      this.editor.commands.hideLinkTooltip();
    },

    addCommands() {
      return {
        setDragHandleBlockId: (attributes) => () => {
          this.storage.dragHandleBlockId = attributes.blockId;
        },

        hideDragHandle: () => () => {
          if (throttleTimeout) {
            clearTimeout(throttleTimeout);
          }
          lastHoverElementId = null;
          this.storage.dragHandleBlockId = '';
          tippyRef?.destroy?.();
          hoverComponent?.ref?.$destroy?.();
        },

        hideLinkTooltip: () => () => {
          if (linkComponent) linkComponent?.destroy();
          if (linkTippy) linkTippy?.destroy?.();
          resetLinkVariables();
        },

        hideBlockMenu: () => () => {
          if (this.editor.storage.MouseOver.dragHandleBlockId) {
            this.editor.commands.hideDragHandle();
            this.editor.commands.setDragHandleBlockId({ blockId: '' });
          }
        },
      };
    },

    addProseMirrorPlugins() {
      return [
        new Plugin({
          key: new PluginKey('hover'),
          props: {
            handleDOMEvents: {
              mousemove: (view, event) => {
                // do nothing if the editor is not editable
                if (!this.editor.view.editable) return false;

                if (throttleTimeout) {
                  clearTimeout(throttleTimeout);
                }
                // when resizing block, do not show hover menu
                if (this.editor.storage.resizing.isResizing) return false;

                throttleTimeout = setTimeout(
                  () =>
                    handleMouseOver(
                      event,
                      this.editor,
                      linkTooltipComponent,
                      blockHoverMenuComponent,
                      getTextNodeFromPoint,
                      draggable
                    ),
                  10
                );
                return true;
              },
            },
          },
        }),
      ];
    },
  });
